import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '../../../../store';
import {
  clearDeviceShadow,
  fetchDeviceById,
  fetchDeviceShadow,
} from '../../../../store/devices/actionCreators';
import { Device, DevicesStore } from '../../../../store/devices/deviceTypes';
import { darkPurple, dimGrey } from '../../../../mainTheme';
import UpdateDeviceChannel from '../../../Devices/components/UpdateDeviceChannel';
import DeviceShadowsConfig from '../../../Devices/components/DeviceShadowsConfig';
import DeviceFaults from '../../../Devices/components/DeviceFaults';

interface TabComponentProps {
  selectedDevice: Device | null;
  isFacilityAdmin?: boolean;
  isInternalPrivileged?: boolean;
  isEngineer?: boolean;
}

/**
 * Engineering sys-admin panels (channel / shadow config / faults) ported from
 * the classic DeviceDetail into the v2 Hub detail (migration Phase 1). Renders
 * nothing unless the viewer is engineering — the tab is only offered to eng, but
 * guard here too so a stray render can't expose it.
 */
const AdvancedTab: React.FC<TabComponentProps> = ({
  selectedDevice,
  isEngineer,
}) => {
  const dispatch = useDispatch<ThunkDispatch<DevicesStore, void, AnyAction>>();
  const [showFaults, setShowFaults] = useState(false);
  const { shadowConfigs, shadowDeviceId } = useSelector(
    (state: RootState) => state.devices,
  );
  const deviceId = selectedDevice?.id;

  // Refetch on every hub switch and clear on unmount — the shadow slice is
  // shared, so gating on `shadowConfigs === null` would show (and let an edit
  // write against) the previous hub's config.
  useEffect(() => {
    if (deviceId == null || !isEngineer) return undefined;
    dispatch(fetchDeviceShadow(deviceId));
    return () => {
      dispatch(clearDeviceShadow());
    };
  }, [deviceId, isEngineer, dispatch]);

  // A slow fetch for the previous hub can resolve after the switch; only
  // render configs the store attributes to the hub on screen.
  const configs = shadowDeviceId === deviceId ? shadowConfigs : null;

  if (!isEngineer || !selectedDevice) {
    return (
      <Typography sx={{ p: 3, color: dimGrey }}>
        No advanced tools available.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: darkPurple, mb: 1 }}>
        Software channel
      </Typography>
      <UpdateDeviceChannel
        device={selectedDevice}
        onUpdated={() => dispatch(fetchDeviceById(selectedDevice.id))}
      />

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: darkPurple }}>
          Device faults
        </Typography>
        <Button variant="outlined" onClick={() => setShowFaults((v) => !v)}>
          {showFaults ? 'Hide faults' : 'View faults'}
        </Button>
      </Box>
      {showFaults && (
        <DeviceFaults
          deviceId={selectedDevice.id}
          onClose={() => setShowFaults(false)}
        />
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ color: darkPurple, mb: 1 }}>
        Device shadow configuration
      </Typography>
      <DeviceShadowsConfig configs={configs} deviceId={selectedDevice.id} />
    </Box>
  );
};

export default AdvancedTab;
